import React, { useState, useRef } from 'react';
import { Button, Space, message, Modal } from 'antd';
import {
  CopyOutlined,
  GoogleOutlined,
  TrophyOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

/**
 * エクスポートボタンコンポーネント
 * - HTMLコピー
 * - Googleドキュメントコピー（HTML形式）
 * - 未完了時は出力後に完了確認ダイアログを表示
 */
const ExportButtons = ({
  uuid,
  blocks,
  images,
  articleTitle,
  showCitations = true,
  citations = [], // 引用データを追加
  size = 'large',
  articleCompleted = false, // 記事が完了済みかどうか
  onRequestComplete, // 完了処理を呼び出すコールバック
}) => {
  const [loading, setLoading] = useState({
    html: false,
    google: false,
  });
  
  // 完了確認ダイアログを表示済みかどうか（セッション内で1回のみ表示）
  const hasShownCompleteDialogRef = useRef(false);

  // citation_idからURLを取得するヘルパー関数
  const getCitationUrl = citationId => {
    const citation = citations.find(
      c => String(c.citation_id || c.id) === String(citationId),
    );
    return citation?.source_url || citation?.url || `#citation-${citationId}`;
  };

  // [n]パターンを実際のURLリンクに変換
  const convertCitationNumbersToLinks = text => {
    if (!showCitations) {
      // 引用番号を削除
      return text.replace(/\[(\d+)\]/g, '');
    }
    
    // [n]を<sup><a href="URL">[n]</a></sup>に変換
    return text.replace(/\[(\d+)\]/g, (match, citationId) => {
      const url = getCitationUrl(citationId);
      return `<sup><a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: none;">${match}</a></sup>`;
    });
  };

  // テキストをHTML変換（Markdown＋カスタムタグ）
  const convertTextToHTML = text => {
    let html = text;

    // 改行削除（Step4.2と同じ）: H2, H3, H4, UL, TABLE の前後の\nと<br/>を削除
    html = html.replace(/(<br\s*\/?>|\n)+(\[H2\])/gi, '$2');
    html = html.replace(/(\[\/H2\])(<br\s*\/?>|\n)+/gi, '$1');
    html = html.replace(/(<br\s*\/?>|\n)+(\[H3\])/gi, '$2');
    html = html.replace(/(\[\/H3\])(<br\s*\/?>|\n)+/gi, '$1');
    html = html.replace(/(<br\s*\/?>|\n)+(\[H4\])/gi, '$2');
    html = html.replace(/(\[\/H4\])(<br\s*\/?>|\n)+/gi, '$1');
    html = html.replace(/(<br\s*\/?>|\n)+(\[UL\])/gi, '$2');
    html = html.replace(/(\[\/UL\])(<br\s*\/?>|\n)+/gi, '$1');
    html = html.replace(/(<br\s*\/?>|\n)+(\[TABLE\])/gi, '$2');
    html = html.replace(/(\[\/TABLE\])(<br\s*\/?>|\n)+/gi, '$1');

    // [H4]閉じタグ自動補完
    html = html.replace(/\[H4\]([^\n]*?)(\n|$)/g, (match, content, lineEnd) => {
      if (content.includes('[/H4]')) {
        return match;
      }
      return `[H4]${content}[/H4]${lineEnd}`;
    });
    html = html.replace(/(\[\/H4\])(\n|<br\s*\/?>)/g, '$1');

    // Markdownフォーマット変換（独自情報のテキストエディタ用）
    // 画像: ![alt](url) → <img src="url" alt="alt" />
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />');
    // リンク: [text](url) → <a href="url">text</a>
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>');
    // 見出し: # text → <h2>text</h2>
    html = html.replace(/^#{1}\s+(.+)$/gm, '<h2>$1</h2>');
    html = html.replace(/^#{2}\s+(.+)$/gm, '<h3>$1</h3>');
    html = html.replace(/^#{3}\s+(.+)$/gm, '<h4>$1</h4>');
    // 箇条書き: - item → <ul><li>item</li></ul>
    html = html.replace(/(?:^- .+$\n?)+/gm, (match) => {
      const items = match.trim().split('\n').map(line => {
        const content = line.replace(/^-\s+/, '');
        return `<li>${content}</li>`;
      }).join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${items}</ul>`;
    });

    // [H2][H3][H4]タグをスタイル付きHTMLに変換
    html = html.replace(/\[H2\](.*?)\[\/H2\]/g, '<h2 style="margin: 2em 0 1em;">$1</h2>');
    html = html.replace(/\[H3\](.*?)\[\/H3\]/g, '<h3 style="margin: 2em 0 1em;">$1</h3>');
    html = html.replace(/\[H4\](.*?)\[\/H4\]/g, '<h4 style="margin: 2em 0 1em;">$1</h4>');

    // [UL][LI]タグをHTMLに変換
    html = html.replace(/\[UL\]([\s\S]*?)\[\/UL\]/g, (match, content) => {
      const items = content.match(/\[LI\](.*?)\[\/LI\]/g);
      if (!items) return match;
      const liTags = items
        .map(item => item.replace(/\[LI\](.*?)\[\/LI\]/, '<li>$1</li>'))
        .join('');
      return `<ul style="margin: 2em 0 1em; padding-left: 24px;">${liTags}</ul>`;
    });

    // [TABLE]タグをHTMLに変換
    html = html.replace(/\[TABLE\]([\s\S]*?)\[\/TABLE\]/g, (match, content) => {
      const rows = content.match(/\[TR\](.*?)\[\/TR\]/g);
      if (!rows) return match;
      const trTags = rows
        .map(row => {
          let cells = row.replace(/\[TR\]/, '').replace(/\[\/TR\]/, '');
          cells = cells.replace(
            /\[TH\](.*?)\[\/TH\]/g,
            '<th style="border: 1px solid #e8e8e8; padding: 12px; background: #fafafa; font-weight: 600;">$1</th>',
          );
          cells = cells.replace(
            /\[TD\](.*?)\[\/TD\]/g,
            '<td style="border: 1px solid #e8e8e8; padding: 12px;">$1</td>',
          );
          return `<tr>${cells}</tr>`;
        })
        .join('');
      return `<table style="width: 100%; border-collapse: collapse; margin: 2em 0 1em;">${trTags}</table>`;
    });

    // [B]タグを太字に
    html = html.replace(/\[B\](.*?)\[\/B\]/g, '<strong>$1</strong>');
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    // 引用番号を変換
    html = convertCitationNumbersToLinks(html);

    return html;
  };

  // Editor.jsのblocksからHTMLを生成
  const generateHTML = (includeTitle = false) => {
    let html = '';

    // タイトルを追加（オプション）
    if (includeTitle && articleTitle) {
      html += `<h1>${articleTitle}</h1>\n\n`;
    }

    // blocksを処理
    if (!blocks || !Array.isArray(blocks)) {
      console.error('[ExportButtons] blocks is not an array:', blocks);
      return html;
    }

    blocks.forEach((block, index) => {
      if (!block || !block.type) return;

      switch (block.type) {
        case 'header': {
          const level = block.data?.level || 2;
          const headerText = block.data?.text || '';
          if (headerText) {
            // テキストを変換（Markdown＋カスタムタグ＋引用番号）
            const convertedText = convertTextToHTML(headerText);
            html += `<h${level}>${convertedText}</h${level}>\n`;
          }
          break;
        }

        case 'paragraph': {
          let paragraphText = block.data?.text || '';
          if (paragraphText) {
            // テキストを変換（Markdown＋カスタムタグ＋引用番号）
            const convertedText = convertTextToHTML(paragraphText);
            html += `<p>${convertedText}</p>\n`;
          }
          break;
        }

        case 'list': {
          const listStyle = block.data?.style || 'unordered';
          const listItems = block.data?.items || [];
          if (listItems.length > 0) {
            const tag = listStyle === 'ordered' ? 'ol' : 'ul';
            html += `<${tag}>\n`;
            listItems.forEach(item => {
              let itemText =
                typeof item === 'string'
                  ? item
                  : item?.content || item?.text || '';
              // テキストを変換（Markdown＋カスタムタグ＋引用番号）
              const convertedText = convertTextToHTML(itemText);
              html += `  <li>${convertedText}</li>\n`;
            });
            html += `</${tag}>\n`;
          }
          break;
        }

        case 'table': {
          const tableData = block.data?.content || [];
          const withHeadings = block.data?.withHeadings || false;
          if (tableData.length > 0) {
            html += `<table border="1" style="border-collapse: collapse; width: 100%;">\n`;

            tableData.forEach((row, rowIndex) => {
              const isHeaderRow = withHeadings && rowIndex === 0;
              const cellTag = isHeaderRow ? 'th' : 'td';
              html += `  <tr>\n`;
              row.forEach(cell => {
                let cellText = cell || '';
                // テキストを変換（Markdown＋カスタムタグ＋引用番号）
                const convertedText = convertTextToHTML(cellText);
                html += `    <${cellTag}>${convertedText}</${cellTag}>\n`;
              });
              html += `  </tr>\n`;
            });
            html += `</table>\n`;
          }
          break;
        }

        case 'pexelsImage': {
          // カスタム画像ブロック
          const imgUrl = block.data?.url || '';
          const imgAlt = block.data?.alt || '';
          const photographer = block.data?.photographer || '';
          const photographerUrl = block.data?.photographer_url || '';

          if (imgUrl) {
            html += `<div style="text-align: center; margin: 20px 0;">\n`;
            html += `  <img src="${imgUrl}" alt="${imgAlt}" style="max-width: 100%; height: auto; display: block; margin: 0 auto;" /><br />\n`;
            if (photographer) {
              html += `  <span style="font-size: 12px; color: #666; display: block; margin-top: 8px;">`;
              html += `Photo by <a href="${photographerUrl}" target="_blank" rel="noopener noreferrer">${photographer}</a> on Pexels`;
              html += `</span>\n`;
            }
            html += `</div>\n`;
          }
          break;
        }

        default:
          // 未知のブロックタイプは無視
          break;
      }

      html += '\n'; // ブロック間に空行
    });

    return html;
  };

  // 完了確認ダイアログを表示
  const showCompleteConfirmDialog = () => {
    // 既に完了済み、コールバックがない、または既に表示済みの場合はスキップ
    if (articleCompleted || !onRequestComplete || hasShownCompleteDialogRef.current) {
      return;
    }
    
    hasShownCompleteDialogRef.current = true;
    
    Modal.confirm({
      title: '記事を完了としてマークしますか？',
      icon: <TrophyOutlined style={{ color: '#faad14' }} />,
      content: (
        <div>
          <p style={{ marginBottom: 12 }}>完了すると以下の特典があります：</p>
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ marginBottom: 4 }}>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              EXPを獲得（ランクアップに貢献）
            </li>
            <li>
              <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
              生成実績としてカウント
            </li>
          </ul>
          <p style={{ marginTop: 12, color: '#8c8c8c', fontSize: 12 }}>
            ※完了後は閲覧モードになります
          </p>
        </div>
      ),
      okText: '完了としてマーク',
      cancelText: 'まだ編集を続ける',
      onOk: () => {
        onRequestComplete();
      },
      onCancel: () => {
        // キャンセル時は次回も表示できるようにリセット
        hasShownCompleteDialogRef.current = false;
      },
    });
  };

  // HTMLコピー
  const handleCopyHTML = async () => {
    try {
      setLoading({ ...loading, html: true });
      const html = generateHTML(false); // タイトルは含めない

      if (!html || html.trim().length === 0) {
        message.warning('出力するコンテンツがありません');
        return;
      }

      await navigator.clipboard.writeText(html);
      message.success('HTMLをクリップボードにコピーしました');
      
      // 出力成功後に完了確認ダイアログを表示
      showCompleteConfirmDialog();
    } catch (error) {
      console.error('HTMLコピーエラー:', error);
      message.error('HTMLのコピーに失敗しました');
    } finally {
      setLoading({ ...loading, html: false });
    }
  };

  // Googleドキュメント用（リッチテキストとしてクリップボードにコピー）
  const handleCopyForGoogle = async () => {
    try {
      setLoading({ ...loading, google: true });

      // HTMLを生成
      const html = generateHTML(false);

      if (!html || html.trim().length === 0) {
        message.warning('出力するコンテンツがありません');
        return;
      }

      // リッチテキストとしてクリップボードにコピー
      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobText = new Blob([html.replace(/<[^>]*>/g, '')], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      });

      await navigator.clipboard.write([clipboardItem]);

      message.success({
        content:
          'リッチテキストをコピーしました。Googleドキュメントに直接ペーストしてください',
        duration: 4,
      });
      
      // 出力成功後に完了確認ダイアログを表示
      showCompleteConfirmDialog();
    } catch (error) {
      console.error('Googleドキュメントコピーエラー:', error);
      
      // フォールバック: 通常のテキストとしてコピー
      try {
        const html = generateHTML(false);
        await navigator.clipboard.writeText(html);
        message.warning({
          content:
            'HTMLテキストとしてコピーしました。Googleドキュメントにペーストしてください',
          duration: 4,
        });
        
        // フォールバックでも成功したら完了確認ダイアログを表示
        showCompleteConfirmDialog();
      } catch (fallbackError) {
        message.error('コピーに失敗しました');
      }
    } finally {
      setLoading({ ...loading, google: false });
    }
  };

  return (
    <Space size="small">
      <Button
        size={size}
        type="primary"
        ghost
        icon={<CopyOutlined />}
        onClick={handleCopyHTML}
        loading={loading.html}
      >
        HTMLコピー
      </Button>
      <Button
        size={size}
        type="primary"
        ghost
        icon={<GoogleOutlined />}
        onClick={handleCopyForGoogle}
        loading={loading.google}
      >
        Google Docs
      </Button>
    </Space>
  );
};

export default ExportButtons;
